import React, { useEffect, useRef, useState } from "react";
import {
  Globe,
  Instagram,
  Map as MapIcon,
  Leaf,
  Phone,
  AtSign,
  Clock,
  Facebook,
  Twitter,
  Music2,
  Loader2,
  Gauge,
} from "lucide-react";
import { fetchDiscoveryStats } from "../lib/api";

// Layout: 2 rows of 5 stats each. Ordered by how essential each datapoint
// is to the sales team (phone/email/website first → social/menu links last).
const FIELDS = [
  { key: "phones",      label: "Phone",     icon: Phone,     color: "text-emerald-400", bg: "bg-emerald-500/20" },
  { key: "emails",      label: "Email",     icon: AtSign,    color: "text-sky-400",     bg: "bg-sky-500/20" },
  { key: "websites",    label: "Website",   icon: Globe,     color: "text-violet-400",  bg: "bg-violet-500/20" },
  { key: "hours",       label: "Hours",     icon: Clock,     color: "text-amber-400",   bg: "bg-amber-500/20" },
  { key: "instagrams",  label: "Instagram", icon: Instagram, color: "text-pink-400",    bg: "bg-pink-500/20" },
  { key: "facebook",    label: "Facebook",  icon: Facebook,  color: "text-blue-400",    bg: "bg-blue-500/20" },
  { key: "twitter",     label: "Twitter/X", icon: Twitter,   color: "text-cyan-400",    bg: "bg-cyan-500/20" },
  { key: "tiktok",      label: "TikTok",    icon: Music2,    color: "text-fuchsia-400", bg: "bg-fuchsia-500/20" },
  { key: "weedmaps",    label: "Weedmaps",  icon: MapIcon,   color: "text-orange-400",  bg: "bg-orange-500/20" },
  { key: "leafly",      label: "Leafly",    icon: Leaf,      color: "text-lime-400",    bg: "bg-lime-500/20" },
];

function pct(n, d) {
  if (!d) return 0;
  return Math.round((n / d) * 100);
}

function Bar({ value, total, color, bg }) {
  const p = pct(value, total);
  return (
    <div className={`h-1.5 w-full rounded-full ${bg} overflow-hidden`}>
      <div
        className={`h-full rounded-full ${color.replace("text-", "bg-")} transition-all duration-700`}
        style={{ width: `${p}%` }}
      />
    </div>
  );
}

/**
 * Dashboard card that visualizes how filled-in the dispensary database is
 * across the 10 most important contact / discovery fields, plus a single
 * roll-up "completeness score" (0–100).
 *
 * Polls every 4s while an enrichment scan is running; otherwise refreshes
 * only on mount.
 */
export function DataCompletenessCard() {
  const [stats, setStats] = useState(null);
  const intervalRef = useRef(null);

  const refresh = async () => {
    try {
      const s = await fetchDiscoveryStats();
      setStats(s);
    } catch {
      /* swallow transient errors */
    }
  };

  useEffect(() => {
    refresh();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  useEffect(() => {
    const running = !!stats?.running_scan;
    if (running && !intervalRef.current) {
      intervalRef.current = setInterval(refresh, 4000);
    } else if (!running && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [stats?.running_scan]);

  if (!stats) {
    return (
      <div
        data-testid="data-completeness-card"
        className="panel p-5 h-[280px] animate-pulse bg-zinc-900/40"
      />
    );
  }

  const total = stats.total_active || 0;
  const score = stats.completeness_score ?? 0;
  const running = stats.running_scan;
  const runProcessed = running?.processed ?? 0;
  const runTotal = running?.total ?? 0;
  const runPct = runTotal ? Math.round((runProcessed / runTotal) * 100) : 0;

  // Pick score color band.
  const scoreColor =
    score >= 80 ? "text-emerald-300"
    : score >= 50 ? "text-amber-300"
    : "text-rose-300";

  return (
    <div data-testid="data-completeness-card" className="panel p-5">
      <div className="flex items-start justify-between mb-5 gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-mono">
            <Gauge className="w-3 h-3 text-emerald-400" />
            Data completeness
          </div>
          <div className="text-sm text-zinc-400 mt-1 leading-snug max-w-xl">
            How much contact data is on file across the{" "}
            <span className="font-mono text-zinc-200">
              {total.toLocaleString()}
            </span>{" "}
            active dispensaries in the database. The score weighs phone,
            email, website, hours, Instagram, and Weedmaps equally.
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div
            data-testid="completeness-score"
            className="flex items-baseline gap-1.5"
            title="Average % across primary contact fields"
          >
            <span className={`text-4xl font-semibold ${scoreColor} font-mono`}>
              {score}
            </span>
            <span className="text-xs text-zinc-500 font-mono">/ 100</span>
          </div>
          {running && (
            <div
              data-testid="discovery-running-pill"
              className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-emerald-200 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2.5 h-7"
            >
              <Loader2 className="w-3 h-3 animate-spin text-emerald-400" />
              Scanning · {runPct}%
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
        {FIELDS.map(({ key, label, icon: Icon, color, bg }) => {
          const value = stats[key] || 0;
          const p = pct(value, total);
          return (
            <div
              key={key}
              data-testid={`completeness-stat-${key}`}
              className="space-y-2"
            >
              <div className="flex items-center gap-1.5">
                <Icon className={`w-3.5 h-3.5 ${color}`} />
                <span className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">
                  {label}
                </span>
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className={`text-xl font-semibold ${color}`}>
                  {value.toLocaleString()}
                </span>
                <span className="text-[10px] font-mono text-zinc-500 ml-auto">
                  {p}%
                </span>
              </div>
              <Bar value={value} total={total} color={color} bg={bg} />
            </div>
          );
        })}
      </div>

      {stats.verified_recent != null && (
        <div className="mt-4 pt-3 border-t border-zinc-800 text-[11px] text-zinc-500 font-mono inline-flex items-center gap-2">
          <span className="text-emerald-300">
            {stats.verified_recent.toLocaleString()}
          </span>
          verified in the last 30 days
        </div>
      )}
    </div>
  );
}

export default DataCompletenessCard;
